import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    this.#observeActiveSlide(this.slideshow?.initialSlide);
  }

  #controller = new AbortController();

  /**
   * Measures the currently observed slide and writes its rendered height to --active-media-height,
   * so slideshow-slides (a flex row that would otherwise size to its tallest slide) can be locked
   * to the active slide only. Fires on observe() and on every subsequent resize of that slide.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    this.style.setProperty('--active-media-height', `${entry.contentRect.height}px`);
  });

  /** @type {HTMLElement | undefined} */
  #observedSlide;

  /** @param {HTMLElement | undefined} slide */
  #observeActiveSlide(slide) {
    if (!(slide instanceof HTMLElement) || slide === this.#observedSlide) return;

    if (this.#observedSlide) this.#resizeObserver.unobserve(this.#observedSlide);
    this.#observedSlide = slide;
    this.#resizeObserver.observe(slide);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Keeps the gallery's reserved height matched to the active slide only.
   * slideshow-slides is a flex row, so left alone its cross size (and so the visible
   * gallery height) is set by the tallest slide in the set even while a shorter one is
   * showing - most noticeable when a portrait video sits alongside portrait product
   * photos, leaving a large gap before the slide it belongs to. --active-media-ratio
   * gives the correct height on first paint (from the slide's own --media-preview-ratio,
   * server-rendered, before any JS runs); --active-media-height (this slide's measured
   * box, kept live by #resizeObserver) is what actually constrains slideshow-slides,
   * since aspect-ratio on slideshow-container alone doesn't stop it sizing to the
   * tallest slide.
   * @param {SlideshowSelectEvent} event - The slideshow:select event.
   */
  #handleSlideshowSelect = (event) => {
    const { slide } = event.detail;
    if (!(slide instanceof HTMLElement)) return;

    const ratio = slide.style.getPropertyValue('--media-preview-ratio');
    if (ratio) this.style.setProperty('--active-media-ratio', ratio);

    this.#observeActiveSlide(slide);
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
